import { Feature, FeatureCollection } from "geojson";
import { Step } from "intro.js";
import GeoJSON from "ol/format/GeoJSON";
import { Feature as OlFeature } from "ol";
import { register } from "ol/proj/proj4";
import proj4 from "proj4";

register(proj4);

// start a download without clicking on a link
export const downloadFile = async (
  url: string,
  filename: string,
  headers?: Headers
) => {
  return new Promise(async (resolve, reject) => {
    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      reject();
      return;
    }

    const blob = await response.blob();
    const urlBlob = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = urlBlob;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    resolve("success");

    // cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(urlBlob);
  });
};

// return the file-extension from a URL
export const getFileExtensionFromURL = (url: string) => {
  return url.split(".").pop();
};

// calculate slop in % from two 2d coordinates
export function calculateSlope(p1: Coordinates2d, p2: Coordinates2d) {
  // calculate elevation
  const deltaX = p2.x - p1.x;
  const deltaY = p2.y - p1.y;

  const slope = (deltaY / deltaX) * 100;
  return slope;
}

export interface Coordinates2d {
  x: number;
  y: number;
}

export interface Coordinates3d extends Coordinates2d {
  z: number;
}

export function transformGeoJSON(
  geojson: Feature | FeatureCollection,
  srid_from: number,
  srid_to: number
): Feature | FeatureCollection {
  const ol_format = new GeoJSON({
    dataProjection: `EPSG:${srid_from}`,
  });
  if (geojson.type == "FeatureCollection") {
    const ol_features = ol_format.readFeatures(JSON.stringify(geojson), {
      dataProjection: `EPSG:${srid_from}`,
    });
    const geojson_transformed = ol_format.writeFeaturesObject(ol_features, {
      dataProjection: `EPSG:${srid_to}`,
      featureProjection: `EPSG:${srid_from}`,
      decimals: 8,
    });
    return geojson_transformed;
  } else if (geojson.type == "Feature") {
    const ol_feature = ol_format.readFeature(JSON.stringify(geojson), {
      dataProjection: `EPSG:${srid_from}`,
    }) as OlFeature;
    const geojson_transformed = ol_format.writeFeatureObject(ol_feature, {
      dataProjection: `EPSG:${srid_to}`,
      featureProjection: `EPSG:${srid_from}`,
      decimals: 8,
    });
    return geojson_transformed;
  }
  return geojson;
}

export function removeItemFromArray<T>(item: T, array: T[]) {
  const index = array.findIndex((i) => i === item);
  if (index !== -1) {
    array.splice(index, 1);
  }
}

export function generateShareURL(imageKey: string, pointTo?: string) {
  const url = new URL(window.location.href);
  url.searchParams.append("imageKey", imageKey);

  if (pointTo) {
    url.searchParams.append("pointTo", pointTo);
  }
  return url;
}

export const addThousandsSeparator = (number: string | number) => {
  if (typeof number === "number") number = number.toString();
  return number.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
};

export function waitForAllElements({
  selectors,
  onReady,
  maxWaitMs = 5000,
  pollIntervalMs = 50,
}: {
  selectors: Step["element"][];
  onReady: () => void;
  maxWaitMs?: number;
  pollIntervalMs?: number;
}) {
  const startTime = Date.now();
  let cancelled = false;
  let timerId: ReturnType<typeof setTimeout> | null = null;

  // Filter out undefined/null selectors right away
  const validSelectors = selectors.filter((s): s is string => !!s);

  const check = () => {
    if (cancelled) return;

    const missingSelectors = validSelectors.filter(
      (selector) => !document.querySelector(selector as string)
    );

    if (missingSelectors.length === 0) {
      onReady();
    } else if (Date.now() - startTime < maxWaitMs) {
      timerId = setTimeout(check, pollIntervalMs);
    } else {
      console.warn(
        "Intro.js: The following target elements were not found within the timeout:",
        missingSelectors
      );
      onReady(); // optionally proceed anyway
    }
  };

  check();

  return () => {
    cancelled = true;
    if (timerId !== null) {
      clearTimeout(timerId);
    }
  };
}

export function findMissingItems(array1: Array<any>, array2: Array<any>) {
  // Get items in array2 but not in array1
  const itemsInArray2ButNotInArray1 = array2.filter(
    (item2) =>
      !array1.some((item1) => JSON.stringify(item1) === JSON.stringify(item2))
  );

  // Get items in array1 but not in array2)
  const ItemsInArray1ButNotInArray2 = array1.filter(
    (item1) =>
      !array2.some((item2) => JSON.stringify(item1) === JSON.stringify(item2))
  );

  return { itemsInArray2ButNotInArray1, ItemsInArray1ButNotInArray2 };
}

export function setIntroductionShownLS(
  platform: "edit" | "view" | "api",
  shown: boolean
) {
  const all = JSON.parse(
    localStorage.getItem(`${platform}-introShown`) || "{}"
  );
  all["introduction"] = shown;
  localStorage.setItem(`${platform}-introShown`, JSON.stringify(all));
}

export function getIntroductionShownLS(
  platform: "edit" | "view" | "api",
  key: "introduction"
): string {
  const all = JSON.parse(
    localStorage.getItem(`${platform}-introShown`) || "{}"
  );
  return all[key];
}

export function getApiKeyHeaders() {
  const apiKey = process.env.API_KEY;
  if (apiKey) {
    return {
      "x-api-key": apiKey,
    };
  } else {
    throw new Error("API_KEY is not defined");
  }
}

export function getAuthorizationHeaders(accessToken: string) {
  if (accessToken) {
    return { Authorization: `Bearer ${accessToken}` };
  } else {
    throw new Error("No Access Token provided");
  }
}

export function getStringSizeInMB(str: string) {
  const bytes = new TextEncoder().encode(str).length;
  const sizeInMB = bytes / (1024 * 1024);
  return sizeInMB.toFixed(2);
}
